import type { SampleRatioResult } from 'helpers/sample-ratio';
import type { SegmentCounts, SegmentsResponse } from './useExperimentSegments';
import { checkSampleRatio } from 'helpers/sample-ratio';

/**
 * Readings for the segments section, derived from the /segments response. Pure, so the
 * thresholds and the split checks are tested without rendering.
 */

/** Below this many visitors per variant a segment's lift is noise; it is dimmed and the lift is not shown. */
export const MIN_SEGMENT_VISITORS = 100;

export type SegmentReading = {
  variantId: number;
  name: string;
  isControl: boolean;
  enrolled: number;
  converters: number;
  /** 0..1 */
  rate: number;
  /** Minor units per enrolled visitor. */
  revenuePerVisitor: number;
  /** Relative, in percent, against the control. Null for the control, and when noLift says why. */
  lift: number | null;
  /** Why there is no lift: a side under the minimum, or a control that converted nobody. */
  noLift: 'too-few' | 'no-baseline' | null;
  tooSmall: boolean;
};

export type SegmentBlock = {
  segment: string;
  label: string;
  readings: SegmentReading[];
  /**
   * Whether this segment's variants split the way the experiment's own id-bearing
   * enrolments did. Not against the configured weights: rebalancing moves those over
   * time, so a segment that filled up at a different time would fail for no fault.
   * Null when the dimension is not checked at all: see blocksOf.
   */
  split: SampleRatioResult | null;
};

const SEGMENT_LABELS: Record<string, string> = {
  Desktop: 'Desktop',
  Mobile: 'Mobile',
  Tablet: 'Tablet',
  Unknown: 'Unknown device',
  new: 'New',
  returning: 'Returning',
  insufficient_history: 'Not enough history yet',
};

export const segmentLabel = (segment: string): string => SEGMENT_LABELS[segment] ?? segment;

type Variant = SegmentsResponse['variants'][number];

type ReadingsParams = { rows: readonly SegmentCounts[]; variants: readonly Variant[] };

export function readingsOf({ rows, variants }: ReadingsParams): SegmentReading[] {
  const counts = new Map(rows.map((row) => [row.variantId, row]));
  const control = variants.find((v) => v.isControl) ?? variants[0];
  const controlCounts = control ? counts.get(control.id) : undefined;
  const controlRate =
    controlCounts && controlCounts.enrolled > 0 ? controlCounts.converters / controlCounts.enrolled : 0;
  const controlTooSmall = (controlCounts?.enrolled ?? 0) < MIN_SEGMENT_VISITORS;

  return variants.map((variant) => {
    const row = counts.get(variant.id);
    const enrolled = row?.enrolled ?? 0;
    const converters = row?.converters ?? 0;
    const rate = enrolled > 0 ? converters / enrolled : 0;
    const tooSmall = enrolled < MIN_SEGMENT_VISITORS;
    const isControl = variant.id === control?.id;
    const noLift = isControl
      ? null
      : tooSmall || controlTooSmall
        ? 'too-few'
        : controlRate === 0
          ? 'no-baseline'
          : null;
    const lift = isControl || noLift ? null : ((rate - controlRate) / controlRate) * 100;

    return {
      variantId: variant.id,
      name: variant.name,
      isControl,
      enrolled,
      converters,
      rate,
      revenuePerVisitor: enrolled > 0 ? (row?.revenue ?? 0) / enrolled : 0,
      lift,
      noLift,
      tooSmall,
    };
  });
}

type BlocksParams = {
  segments: SegmentsResponse['byDevice'];
  overall: readonly SegmentCounts[];
  variants: readonly Variant[];
  /**
   * Only for segments that fill up across the whole run, like device. New vs returning
   * are slices of time (everyone enrolled before the history is long enough is "not
   * enough history"), so after a weight change their splits differ from the whole run's
   * for no fault, and a check would only raise false alarms.
   */
  checkSplit: boolean;
};

export function blocksOf({ segments, overall, variants, checkSplit }: BlocksParams): SegmentBlock[] {
  const overallEnrolled = new Map(overall.map((row) => [row.variantId, row.enrolled]));

  return segments.map(({ segment, rows }) => {
    const enrolled = new Map(rows.map((row) => [row.variantId, row.enrolled]));
    return {
      segment,
      label: segmentLabel(segment),
      readings: readingsOf({ rows, variants }),
      split: checkSplit
        ? checkSampleRatio(
            variants.map((v) => ({
              id: v.id,
              name: v.name,
              weight: overallEnrolled.get(v.id) ?? 0,
              sessions: enrolled.get(v.id) ?? 0,
            })),
          )
        : null,
    };
  });
}

export type Coverage = {
  /** Enrolments with an id over the counter's enrolments, 0..1, or null with no counter. */
  enrolmentShare: number | null;
  /** Conversion events with an id over all of them since collection began, 0..1, or null with none. */
  eventShare: number | null;
  conflicted: number;
};

/**
 * No split check here, on purpose. The counter covers the whole run and the id-bearing
 * rows only the time since the flag went on, so after a weight rebalance (or on a
 * deployed experiment, whose returning visitors register twice in the counter) the two
 * legitimately differ, and a check between them only raises false alarms. The segments'
 * own checks compare like with like: see blocksOf.
 */
export function coverageOf(coverage: SegmentsResponse['coverage']): Coverage {
  const counted = coverage.variants.reduce((sum, v) => sum + v.counted, 0);
  const withId = coverage.variants.reduce((sum, v) => sum + v.withId, 0);

  return {
    enrolmentShare: counted > 0 ? withId / counted : null,
    eventShare: coverage.events.total > 0 ? coverage.events.withId / coverage.events.total : null,
    conflicted: coverage.variants.reduce((sum, v) => sum + v.conflicted, 0),
  };
}

export type ReturnVisitReading = {
  variantId: number;
  name: string;
  isControl: boolean;
  matured: number;
  /** Return visits per matured visitor. */
  perVisitor: number;
  /** Share with at least one return visit, 0..1. */
  share: number;
  tooSmall: boolean;
};

type ReturnVisitsParams = { rows: SegmentsResponse['returnVisits']; variants: readonly Variant[] };

export function returnVisitReadingsOf({ rows, variants }: ReturnVisitsParams): ReturnVisitReading[] {
  const byVariant = new Map(rows.map((row) => [row.variantId, row]));
  return variants.map((variant) => {
    const row = byVariant.get(variant.id);
    const matured = row?.matured ?? 0;
    return {
      variantId: variant.id,
      name: variant.name,
      isControl: variant.isControl,
      matured,
      perVisitor: matured > 0 ? (row?.returnVisits ?? 0) / matured : 0,
      share: matured > 0 ? (row?.withReturn ?? 0) / matured : 0,
      tooSmall: matured < MIN_SEGMENT_VISITORS,
    };
  });
}
